/**
 * The icon set, taken from the design system's v7 preview.
 *
 * The preview itself is deliberately **not** in this repository — it is a rough
 * sample and `design/` stays pinned to the v1.2 pack. But the geometry only
 * existed inside that one HTML file, so a note saying "use the sample's icons"
 * would have pointed at something the next session cannot open. The data is
 * here for that reason; nothing else about the sample was imported.
 *
 * Every glyph is a single path on a 24×24 grid, stroked rather than filled, so
 * one `.icon` rule sizes and colours all of them and `currentColor` makes them
 * inherit from whatever they sit in — including the violet rail, where the
 * foreground is not the page's. Two are circles instead (`grip`, `more`),
 * because a six-dot handle and an ellipsis are dots, not strokes.
 *
 * They are decorative by default: a control that contains only an icon has to
 * carry its own accessible name, and `icon()` marks the SVG `aria-hidden` so a
 * screen reader reads that name rather than announcing an image beside it.
 */

/** Stroked glyphs. 24×24, `fill: none`, `stroke: currentColor`. */
const PATHS = {
  plus: "M12 5v14M5 12h14",
  close: "m6 6 12 12M18 6 6 18",
  moon: "M20.7 13A8.7 8.7 0 0 1 11 3.3 8.8 8.8 0 1 0 20.7 13Z",
  sun: "M12 3V1M12 23v-2M3 12H1M23 12h-2M4.2 4.2l1.5 1.5M18.3 18.3l1.5 1.5M4.2 19.8l1.5-1.5M18.3 5.7l1.5-1.5M16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0",
  inbox: "m4 4-2 11v5h20v-5L20 4ZM2 15h6l2 3h4l2-3h6",
  calendar: "M4 5h16v16H4ZM4 10h16M8 3v4M16 3v4",
  check: "m5 12 4 4L19 6",
  search: "M20 20l-5-5M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0",
  cloud: "M7 18H6a4 4 0 1 1 1-7 6 6 0 0 1 11-2 4.5 4.5 0 0 1 0 9h-1M9 17l3 3 5-5",
  // Not from the preview, unlike everything around it: the pack has a cloud
  // with a checkmark and nothing for the state before it. Derived from `cloud`
  // rather than drawn fresh so the pair reads as one family — the identical
  // outline, with the gap the checkmark sat in closed up (`h-1` becomes `H7`).
  cloudPending: "M7 18H6a4 4 0 1 1 1-7 6 6 0 0 1 11-2 4.5 4.5 0 0 1 0 9H7",
  focus: "M8 3H3v5M16 3h5v5M3 16v5h5M21 16v5h-5M16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0",
  play: "m9 5 10 7-10 7Z",
  list: "M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01",
  chevron: "m9 5 7 7-7 7",
  alert: "m12 3 10 18H2ZM12 9v5M12 17h.01",
  up: "m6 14 6-6 6 6",
  down: "m6 10 6 6 6-6",
  edit: "m16 3 5 5-12 12-6 1 1-6ZM14 5l5 5",
  // Not from the preview either: the pack has no visibility glyph. The
  // completed-items control (C01) wants one eye for both of its states — the
  // label carries "Hide" or "Show", so a glyph that flipped with it would say
  // the same thing twice and be read as the current state by anyone who missed
  // the word. Drawn to the family's stroke and 24-grid, with `search`'s circle
  // for the pupil.
  eye: "M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12ZM15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0",
  // Not from the preview either: the pack has nothing for a shopping list, and
  // the index (L03) and the rail (S02) lead every list with its kind. A
  // trolley — handle, basket, two wheels — drawn to the family's stroke and
  // 24-grid, with the wheels as the `h.01` dots the `list` glyph uses.
  cart: "M3 4h2l2.4 11h11.2L21 8H7M9 20h.01M17 20h.01",
  // Not from the preview either: the item actions menu (g18) gives each of its
  // entries a glyph, and the pack has none for inserting or deleting. The two
  // inserts are the row as a line and `plus` shrunk beside it, on the side the
  // new item lands, so above and below read as a pair and differ at a glance.
  // The bin is a lid, a body and two ticks, drawn to the family's stroke and
  // 24-grid like the rest.
  insertAbove: "M4 20h16M12 4v8M8 8h8",
  insertBelow: "M4 4h16M12 12v8M8 16h8",
  trash: "M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6",
  share: "M12 16V3M7 8l5-5 5 5M5 12H3v9h18v-9h-2",
  // Not from the preview either: Settings (g48) is reached by a gear alone in
  // the rail, and the pack has none. Eight flat teeth on a 7.5 ring around
  // `eye`'s 3-radius hub, drawn to the family's stroke and 24-grid.
  gear: "M10.1 4.8L10.3 2.2L13.7 2.2L13.9 4.8A7.5 7.5 0 0 1 15.8 5.5L17.7 3.8L20.2 6.3L18.5 8.3A7.5 7.5 0 0 1 19.2 10.1L21.8 10.3L21.8 13.7L19.2 13.9A7.5 7.5 0 0 1 18.5 15.7L20.2 17.7L17.7 20.2L15.8 18.5A7.5 7.5 0 0 1 13.9 19.2L13.7 21.8L10.3 21.8L10.1 19.2A7.5 7.5 0 0 1 8.3 18.5L6.3 20.2L3.8 17.7L5.5 15.8A7.5 7.5 0 0 1 4.8 13.9L2.2 13.7L2.2 10.3L4.8 10.1A7.5 7.5 0 0 1 5.5 8.3L3.8 6.3L6.3 3.8L8.2 5.5A7.5 7.5 0 0 1 10.1 4.8ZM15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0",
  // g56, app-owned like those above. Upcoming is a future time view, and
  // the chevron it borrowed said "go into this" instead: `calendar` with its
  // lower-right corner opened for a forward arrow.
  calendarNext: "M20 12V5H4v16h8M4 10h16M8 3v4M16 3v4M15 18h6M18 15l3 3-3 3",
  // The to-do kind. `check` read as a completed row; two empty boxes and their
  // lines say "a list of things to tick" with no tick in it.
  checklist: "M4 5h5v5H4ZM4 14h5v5H4ZM13 7.5h7M13 16.5h7",
  // Clear completed removes rows, and a tick beside it read as "complete
  // these". List lines, the last cut short by a cross; not `trash`, which
  // stays Delete list's, and not `close`, which dismisses.
  listClear: "M3 6h13M3 12h9M3 18h6M14 15l6 6M20 15l-6 6",
  // Order by category (g81) arranges a list by its categories. `listClear`'s lines, each
  // shorter than the last, with a down arrow beside them for "put in order";
  // not `grip`, which stays Reorder's hand-moved rows.
  sort: "M3 6h11M3 12h8M3 18h5M18 5v14M15 16l3 3 3-3",
  // The note viewer (g77). View note is a page with a folded corner and two
  // lines of text; not `eye`, which stays Show or Hide completed. The viewer's
  // way back is `chevron` turned to face left.
  note: "M6 3h9l4 4v14H6ZM15 3v4h4M9 12h7M9 16h7",
  back: "m15 5-7 7 7 7",
  undo: "M9 14 4 9l5-5M4 9h10a6 6 0 0 1 0 12h-3",
  // Activity (g110): a clock face whose rim turns back on itself, for who
  // changed what and when. Not `undo`, which acts; this only reads.
  history: "M3 12a9 9 0 1 0 2.6-6.4L3 8M3 3v5h5M12 7v5l3 2",
} as const;

/** Dot glyphs, filled rather than stroked. */
const DOTS = {
  grip: [
    [9, 5],
    [15, 5],
    [9, 12],
    [15, 12],
    [9, 19],
    [15, 19],
  ],
  more: [
    [5, 12],
    [12, 12],
    [19, 12],
  ],
} as const;

const DOT_RADIUS = { grip: 1.4, more: 1.5 } as const;

export type IconName = keyof typeof PATHS | keyof typeof DOTS;

export const ICON_NAMES: IconName[] = [
  ...(Object.keys(PATHS) as (keyof typeof PATHS)[]),
  ...(Object.keys(DOTS) as (keyof typeof DOTS)[]),
];

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * Built with `createElementNS`, and **not** with `el()` from `lib/dom.ts`.
 *
 * The trap is `createElement`, which is what `el()` calls: it has no namespace
 * argument, so `el("svg")` produces an element in the XHTML namespace that is
 * not an `SVGElement` and never renders — silently, with no error to say why.
 * Every other element in this codebase goes through `el()`, so that is the
 * mistake actually within reach here.
 *
 * `innerHTML` is *not* the trap, despite being the obvious suspect: the HTML
 * parser switches to foreign-content mode for `<svg>`, so markup assigned to an
 * HTML parent does come out correctly namespaced and does render. Measured in
 * Chromium rather than assumed, because an earlier version of this comment said
 * the opposite and was wrong.
 */
export function icon(name: IconName): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("class", "icon");
  svg.setAttribute("viewBox", "0 0 24 24");
  // Decorative. The control around it carries the name.
  svg.setAttribute("aria-hidden", "true");

  if (name in DOTS) {
    const key = name as keyof typeof DOTS;
    const group = document.createElementNS(SVG_NS, "g");
    group.setAttribute("fill", "currentColor");
    group.setAttribute("stroke", "none");
    for (const [cx, cy] of DOTS[key]) {
      const circle = document.createElementNS(SVG_NS, "circle");
      circle.setAttribute("cx", String(cx));
      circle.setAttribute("cy", String(cy));
      circle.setAttribute("r", String(DOT_RADIUS[key]));
      group.append(circle);
    }
    svg.append(group);
    return svg;
  }

  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("d", PATHS[name as keyof typeof PATHS]);
  svg.append(path);
  return svg;
}
